import { FC, useEffect, useRef, useState } from "react";
import {
  FormController,
  MuiBox,
  MuiDateField,
  MuiGrid,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
} from "../../../../../components";
import { GenericAccountsSearchResultWrapper } from "./GenericAccountsSearchResult.style";

import SearchIcon from "@mui/icons-material/Search";

import { InputAdornment } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useGetCountryListForDropdowns } from "../../../../../api/services/client/clientApis";
import { GenericAccountSearchResponse } from "../../../../../api/services/client/models";
import { useGetSolutionOffering } from "../../../../../api/services/programTemplate/programTemplateApis";
import MuiEllipseLabel from "../../../../../components/ellipseLabel/MuiEllipseLabel";
import { MuiLink } from "../../../../../components/link/MuiLink";
import {
  STATUS_BADGE_TYPE,
  StatusBadge,
} from "../../../../../components/statusBadge/StatusBadge";
import {
  EMPTY_VALUE_DASH,
  LANGUAGE_CODE,
  MODULE_PERMISSIONS,
  PAGE_IDS,
} from "../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../helpers/constants/countryDateFormat";
import { MAX_LENGTH_50 } from "../../../../../helpers/constants/validations";
import { LabelValue } from "../../../../../helpers/types/common";
import {
  checkFilterMatch,
  compareDates,
  convertToUserFormat,
  getFullName,
  getGenericAccountStatusFromExpirationDate,
  getValueOrBlank,
  isNotEmpty,
  setApiDataToDropDownOption,
} from "../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { toggleToast } from "../../../../../redux/slices/appState/appStateSlice";
import { GenericAccountsSearchResultFormKey } from "../../modelsAndSchemas/Models";
import { genericAccountsSearchSiteCoreKeys } from "../../siteCore/SiteCoreKeys";

const pageId = PAGE_IDS.GENERIC_ACCOUNTS_RESULT;

interface GenericAccountsSearchResultProps {
  rows: GenericAccountSearchResponse[];
  isSearch: boolean;
  onEditAccountClick?: (id: string) => void;
  onDeleteAccountClick?: (id: string) => void;
  handleViewAccountClick?: (id: string) => void;
}

export const GenericAccountsSearchResult: FC<
  GenericAccountsSearchResultProps
> = ({
  rows,
  isSearch,
  handleViewAccountClick,
  onEditAccountClick,
  onDeleteAccountClick,
}) => {
  const dispatch = useDispatch();
  const tableRef = useRef<{
    resetPagination: (shouldResetRowsPerPage?: boolean) => void;
  }>(null);
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );

  const {
    fetchLabel,
    commonLabels,
    fetchComponentTitle,
    fetchDropdownValues,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();

  const { commonARIALabel } = useNavigationSiteCore();
  const { hasModulePermission } = usePermissions();

  const { data: solutionOfferings, isError: isSolutionOfferingError } =
    useGetSolutionOffering(LANGUAGE_CODE.EN_US, true);

  const { data: countryListData, isError } = useGetCountryListForDropdowns();

  const [filteredResults, setFilteredResults] = useState<
    GenericAccountSearchResponse[]
  >(rows || []);
  const [uniqueRequestedBy, setUniqueRequestedBy] = useState<LabelValue[]>([]);
  const [uniqueNextProgramClass, setUniqueNextProgramClass] = useState<
    LabelValue[]
  >([]);

  const resetPagination = (shouldResetRowsPerPage: boolean = false) => {
    if (tableRef.current) {
      tableRef.current.resetPagination(shouldResetRowsPerPage);
    }
  };

  const searchResultRows = rows || [];

  useEffect(() => {
    if (Array.isArray(searchResultRows)) {
      searchResultRows.forEach((result) => {
        result.genericAccountName = result?.name;
        result.clientName = result?.client?.name ?? ""; // Replace with appropriate id and name values
        result.purpose = result?.purposeId ?? "";
        result.assignedAccounts = result.numberOfAccounts?.assigned;
        result.registeredAccounts = result.numberOfAccounts?.registered;
        result.nextView = result.solutionOffering?.name ?? "";
        result.nextProgramClass = result.programClass?.name ?? "";
        result.country = result.country ?? "";
        result.genericAccountStatus = result.status;
        result.countryName = result?.country?.name ?? ""; // Replace with appropriate id and name values
      });
    }

    reset();
    resetPagination(true);

    setFilteredResults([...searchResultRows]);

    if (searchResultRows && isNotEmpty(searchResultRows)) {
      setUniqueRequestedBy(
        Array.from(
          searchResultRows
            .reduce((map, row) => {
              const email = row.requestedByUserId?.email ?? "";
              const name = getFullName(
                row.requestedByUserId?.firstName,
                row.requestedByUserId?.lastName,
                true,
              );
              if (email && name && !map.has(email)) {
                map.set(email, { value: email, label: name });
              }
              return map;
            }, new Map<string, LabelValue>())
            .values(),
        ),
      );
      setUniqueNextProgramClass(
        Array.from(
          new Set(searchResultRows.map((row) => row.programClass?.name ?? "")),
        )
          .filter((value) => value)
          .map((value) => ({ value, label: value })),
      );
    }
  }, [searchResultRows]);

  useEffect(() => {
    if (isSolutionOfferingError || isError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
    }
  }, [isSolutionOfferingError, isError]);

  const { control, watch, reset, getValues, setValue } = useForm({
    mode: "all",
    defaultValues: {
      [GenericAccountsSearchResultFormKey.genericAccountName]: "",
      [GenericAccountsSearchResultFormKey.clientName]: "",
      [GenericAccountsSearchResultFormKey.purpose]: "",
      [GenericAccountsSearchResultFormKey.registrationCode]: "",
      [GenericAccountsSearchResultFormKey.assignedAccounts]: "",
      [GenericAccountsSearchResultFormKey.registeredAccounts]: "",
      [GenericAccountsSearchResultFormKey.nextView]: "",
      [GenericAccountsSearchResultFormKey.nextProgramClass]: "",
      [GenericAccountsSearchResultFormKey.genericAccountStatus]: "",
      [GenericAccountsSearchResultFormKey.country]: "",
      [GenericAccountsSearchResultFormKey.expirationDate]: "",
      [GenericAccountsSearchResultFormKey.requestedBy]: "",
      [GenericAccountsSearchResultFormKey.tablePaginationPageNo]: 1,
    },
  });

  useEffect(() => {
    // Logic to filter the records based on the search params in the result section
    const {
      genericAccountName,
      clientName,
      purpose,
      registrationCode,
      nextView,
      country,
      expirationDate,
      genericAccountStatus,
      nextProgramClass,
      requestedBy,
    } = getValues();
    if (searchResultRows && isNotEmpty(searchResultRows)) {
      const myData = [...searchResultRows].filter((item) => {
        const genericAccountNameMatch = checkFilterMatch(
          genericAccountName,
          item?.name,
        );

        const registrationCodeMatch = checkFilterMatch(
          registrationCode,
          getValueOrBlank(item?.registrationCode),
        );

        const clientNameMatch = checkFilterMatch(
          clientName,
          getValueOrBlank(item.clientName),
        );

        const purposeMatch = checkFilterMatch(
          purpose,
          getValueOrBlank(item.purposeId),
        );

        const nextViewMatch = checkFilterMatch(
          nextView,
          getValueOrBlank(item?.solutionOffering?.id),
        );

        const countryMatch = checkFilterMatch(
          country,
          getValueOrBlank(item?.country?.id),
        );

        const genericAccountStatusMatch =
          !genericAccountStatus ||
          item.genericAccountStatus
            ?.toLocaleLowerCase()
            .includes(genericAccountStatus.toLowerCase());

        const isDateMatch = compareDates(
          getValueOrBlank(expirationDate),
          getValueOrBlank(item?.expirationDate),
          getValueOrBlank(userCountryDateFormat),
        );

        const nextProgramClassMatch = checkFilterMatch(
          nextProgramClass,
          getValueOrBlank(item.programClass?.name),
        );

        const requestedByMatch = checkFilterMatch(
          requestedBy,
          getValueOrBlank(item.requestedByUserId?.email),
        );
        return (
          genericAccountNameMatch &&
          registrationCodeMatch &&
          clientNameMatch &&
          purposeMatch &&
          nextViewMatch &&
          countryMatch &&
          genericAccountStatusMatch &&
          isDateMatch &&
          nextProgramClassMatch &&
          requestedByMatch
        );
      });

      setFilteredResults([...myData]);
      resetPagination();
    }
  }, [
    watch(GenericAccountsSearchResultFormKey.genericAccountName),
    watch(GenericAccountsSearchResultFormKey.clientName),
    watch(GenericAccountsSearchResultFormKey.registrationCode),
    watch(GenericAccountsSearchResultFormKey.purpose),
    watch(GenericAccountsSearchResultFormKey.nextView),
    watch(GenericAccountsSearchResultFormKey.expirationDate),
    watch(GenericAccountsSearchResultFormKey.genericAccountStatus),
    watch(GenericAccountsSearchResultFormKey.nextProgramClass),
    watch(GenericAccountsSearchResultFormKey.requestedBy),
    watch(GenericAccountsSearchResultFormKey.country),
  ]);

  const columns = [
    {
      id: GenericAccountsSearchResultFormKey.genericAccountName,
      label: fetchLabel(
        genericAccountsSearchSiteCoreKeys.searchGenericAccountResults,
        genericAccountsSearchSiteCoreKeys.genericAccountNameResults,
      ),
      render: (row: any) =>
        !row.genericAccountName ? null : (
          <MuiLink
            onClick={() => {
              if (row.id && handleViewAccountClick) {
                handleViewAccountClick(row?.id);
              }
            }}
          >
            <MuiBox>
              <MuiEllipseLabel
                labelTypoClassName="Link"
                text={row.genericAccountName}
                labelTypoVariant="h3"
                placement="bottom-start"
                pointerCursor
              />
            </MuiBox>
          </MuiLink>
        ),
      isSortVisible: true,
    },
    {
      id: GenericAccountsSearchResultFormKey.clientName,
      label: fetchLabel(
        genericAccountsSearchSiteCoreKeys.searchGenericAccountResults,
        genericAccountsSearchSiteCoreKeys.clientResults,
      ),
      render: (row: any) => (
        <MuiBox className="client">
          <MuiEllipseLabel
            labelTypoClassName="Link"
            text={row.clientName}
            labelTypoVariant="h3"
            placement="bottom-start"
            pointerCursor
          />
        </MuiBox>
      ),
      isSortVisible: true,
      className: "cell-client",
    },
    {
      id: GenericAccountsSearchResultFormKey.purpose,
      label: fetchLabel(
        genericAccountsSearchSiteCoreKeys.searchGenericAccountResults,
        genericAccountsSearchSiteCoreKeys.purposeResults,
      ),
      render: (row: any) => (
        <MuiBox className="purpose">
          <MuiTypography variant="h3" className="Link">
            {fetchValueByKeyOfDropdown(
              genericAccountsSearchSiteCoreKeys.searchGenericAccountResults,
              genericAccountsSearchSiteCoreKeys.purposeListResults,
              row.purposeId,
            ) ?? row.purposeId}
          </MuiTypography>
        </MuiBox>
      ),
      isSortVisible: true,
    },
    {
      id: GenericAccountsSearchResultFormKey.registrationCode,
      label: fetchLabel(
        genericAccountsSearchSiteCoreKeys.searchGenericAccountResults,
        genericAccountsSearchSiteCoreKeys.registrationCode,
      ),
      isSortVisible: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="Link">
            {row.registrationCode}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: GenericAccountsSearchResultFormKey.assignedAccounts,
      label: fetchLabel(
        genericAccountsSearchSiteCoreKeys.searchGenericAccountResults,
        genericAccountsSearchSiteCoreKeys.assignedAccounts,
      ),
      isSortVisible: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="Link">
            {row.assignedAccounts}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: GenericAccountsSearchResultFormKey.registeredAccounts,
      label: fetchLabel(
        genericAccountsSearchSiteCoreKeys.searchGenericAccountResults,
        genericAccountsSearchSiteCoreKeys.registeredAccounts,
      ),
      isSortVisible: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="Link">
            {row.registeredAccounts}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: GenericAccountsSearchResultFormKey.nextView,
      label: fetchLabel(
        genericAccountsSearchSiteCoreKeys.searchGenericAccountResults,
        genericAccountsSearchSiteCoreKeys.nextView,
      ),
      isSortVisible: true,
      render: (row: any) => (
        <MuiBox className="nextView">
          <MuiTypography variant="h3" className="Link">
            {row.nextView}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: GenericAccountsSearchResultFormKey.nextProgramClass,
      label: fetchLabel(
        genericAccountsSearchSiteCoreKeys.searchGenericAccountResults,
        genericAccountsSearchSiteCoreKeys.nextProgramClass,
      ),
      isSortVisible: true,
      render: (row: any) => (
        <MuiBox className="nextProgramClass">
          <MuiTypography variant="h3" className="Link">
            {row.programClass?.name}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: GenericAccountsSearchResultFormKey.genericAccountStatus,
      label: fetchLabel(
        genericAccountsSearchSiteCoreKeys.searchGenericAccountResults,
        genericAccountsSearchSiteCoreKeys.genericAccountStatus,
      ),
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="Link">
            <StatusBadge
              label={fetchValueByKeyOfDropdown(
                genericAccountsSearchSiteCoreKeys.searchGenericAccountResults,
                genericAccountsSearchSiteCoreKeys.genericAccountStatusListResults,
                getGenericAccountStatusFromExpirationDate(
                  row?.genericAccountStatus,
                  row?.expirationDate,
                ) ?? STATUS_BADGE_TYPE.active,
              )}
              status={getGenericAccountStatusFromExpirationDate(
                row?.genericAccountStatus,
                row?.expirationDate,
              )}
            />
          </MuiTypography>
        </MuiBox>
      ),
      isSortVisible: true,
    },
    {
      id: GenericAccountsSearchResultFormKey.country,
      label: fetchLabel(
        genericAccountsSearchSiteCoreKeys.searchGenericAccountResults,
        genericAccountsSearchSiteCoreKeys.country,
      ),
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="Link">
            {row?.country?.name}
          </MuiTypography>
        </MuiBox>
      ),
      isSortVisible: true,
    },
    {
      id: GenericAccountsSearchResultFormKey.expirationDate,
      label: fetchLabel(
        genericAccountsSearchSiteCoreKeys.searchGenericAccountResults,
        genericAccountsSearchSiteCoreKeys.expirationDate,
      ),
      isSortVisible: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="Link">
            {isNotEmpty(row.expirationDate)
              ? convertToUserFormat(row.expirationDate, userCountryDateFormat)
              : EMPTY_VALUE_DASH}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: GenericAccountsSearchResultFormKey.requestedBy,
      label: fetchLabel(
        genericAccountsSearchSiteCoreKeys.searchGenericAccountResults,
        genericAccountsSearchSiteCoreKeys.requestedBy,
      ),
      isSortVisible: true,
      render: (row: any) => (
        <MuiBox className="requestedBy">
          <MuiTypography variant="h3" className="Link">
            {getFullName(
              row.requestedByUserId?.firstName,
              row.requestedByUserId?.lastName,
              true,
            )}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: "action",
      label: fetchLabel(
        genericAccountsSearchSiteCoreKeys.searchGenericAccountResults,
        genericAccountsSearchSiteCoreKeys.action,
      ),
      className: "cell-actions",
      render: (row: any) => {
        let menuItems = [];

        if (hasModulePermission(MODULE_PERMISSIONS.GENERIC_ACCOUNT_EDIT)) {
          menuItems.push({
            onClick: () => onEditAccountClick?.(row.id),
            dataTestId: `edit-generic-account`,
            label: commonLabels.edit,
          });
        }
        if (hasModulePermission(MODULE_PERMISSIONS.GENERIC_ACCOUNT_DELETE)) {
          if (row.numberOfAccounts?.registered === 0) {
            menuItems.push({
              onClick: () => onDeleteAccountClick?.(row.id),
              dataTestId: `delete-activity`,
              label: commonLabels.delete,
            });
          }
        }
        return (
          <MuiBox>
            <MuiOptionMenu menuItems={menuItems} />
          </MuiBox>
        );
      },
    },
  ];

  const renderSearchIcon = () => (
    <InputAdornment position="start">
      <SearchIcon />
    </InputAdornment>
  );

  const tableHeaderForm = [
    <FormController
      key={GenericAccountsSearchResultFormKey.genericAccountName}
      control={control}
      name={GenericAccountsSearchResultFormKey.genericAccountName}
    >
      <MuiTextField
        id={`${pageId}-${GenericAccountsSearchResultFormKey.genericAccountName}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        showCloseIcon={true}
        data-testid="company-input"
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      key={GenericAccountsSearchResultFormKey.clientName}
      control={control}
      name={GenericAccountsSearchResultFormKey.clientName}
    >
      <MuiTextField
        id={`${pageId}-${GenericAccountsSearchResultFormKey.clientName}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        showCloseIcon={true}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      key={GenericAccountsSearchResultFormKey.purpose}
      control={control}
      name={GenericAccountsSearchResultFormKey.purpose}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption={true}
        value=""
        id={`${pageId}-${GenericAccountsSearchResultFormKey.purpose}`}
        placeholder={commonLabels.selectPlaceHolder}
        options={fetchDropdownValues(
          genericAccountsSearchSiteCoreKeys.searchGenericAccountResults,
          genericAccountsSearchSiteCoreKeys.purposeListResults,
        )}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,

    <FormController
      key={GenericAccountsSearchResultFormKey.registrationCode}
      control={control}
      name={GenericAccountsSearchResultFormKey.registrationCode}
    >
      <MuiTextField
        id={`${pageId}-${GenericAccountsSearchResultFormKey.registrationCode}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        showCloseIcon={true}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <></>,
    <></>,
    <FormController
      key={GenericAccountsSearchResultFormKey.nextView}
      control={control}
      name={GenericAccountsSearchResultFormKey.nextView}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption={true}
        options={setApiDataToDropDownOption(solutionOfferings)}
        value=""
        id={`${pageId}-${GenericAccountsSearchResultFormKey.nextView}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      key={GenericAccountsSearchResultFormKey.nextProgramClass}
      control={control}
      name={GenericAccountsSearchResultFormKey.nextProgramClass}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption={true}
        options={uniqueNextProgramClass}
        value=""
        id={`${pageId}-${GenericAccountsSearchResultFormKey.nextProgramClass}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      key={GenericAccountsSearchResultFormKey.genericAccountStatus}
      control={control}
      name={GenericAccountsSearchResultFormKey.genericAccountStatus}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption={true}
        options={fetchDropdownValues(
          genericAccountsSearchSiteCoreKeys.searchGenericAccountResults,
          genericAccountsSearchSiteCoreKeys.genericAccountStatusListResults,
        )}
        value=""
        id={`${pageId}-${GenericAccountsSearchResultFormKey.genericAccountStatus}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      key={GenericAccountsSearchResultFormKey.country}
      control={control}
      name={GenericAccountsSearchResultFormKey.country}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption={true}
        options={setApiDataToDropDownOption(countryListData, true)}
        value=""
        id={`${pageId}-${GenericAccountsSearchResultFormKey.country}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
        showSearch
        searchPlaceholder={commonLabels.search}
      />
    </FormController>,
    <FormController
      key={GenericAccountsSearchResultFormKey.expirationDate}
      control={control}
      name={GenericAccountsSearchResultFormKey.expirationDate}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDateField
          format={userCountryDateFormat}
          valueFormat={userCountryDateFormat}
          id={`${pageId}-${GenericAccountsSearchResultFormKey.expirationDate}`}
          clearable
          name={GenericAccountsSearchResultFormKey.expirationDate}
          value={
            getValues(GenericAccountsSearchResultFormKey.expirationDate) !== ""
              ? dayjs(
                  getValues(GenericAccountsSearchResultFormKey.expirationDate),
                  userCountryDateFormat,
                )
              : null
          }
          onChange={(newValue) => {
            setValue(
              GenericAccountsSearchResultFormKey.expirationDate,
              newValue?.format(userCountryDateFormat) ?? "",
            );
          }}
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,
    <FormController
      key={GenericAccountsSearchResultFormKey.requestedBy}
      control={control}
      name={GenericAccountsSearchResultFormKey.requestedBy}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption={true}
        options={uniqueRequestedBy}
        value=""
        id={`${pageId}-${GenericAccountsSearchResultFormKey.requestedBy}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,

    <></>,
  ];

  return (
    <GenericAccountsSearchResultWrapper>
      <MuiGrid container data-testid="client-result">
        <MuiGrid size={{ xs: 12 }} className="form-header">
          <MuiTypography variant="h2SemiBold" className="form-header-left">
            {fetchComponentTitle(
              genericAccountsSearchSiteCoreKeys.searchGenericAccountResults,
            )}
          </MuiTypography>
        </MuiGrid>
        <MuiGrid container>
          <MuiGrid size={{ xs: 12 }} className="header-right">
            <MuiTable
              ref={tableRef}
              pageId={pageId}
              tableHeaderForm={tableHeaderForm}
              columns={columns}
              tableResults={[...filteredResults]}
              noDataInApi={isSearch}
              emptyMessageColSpan={8}
              emptyMessage={commonLabels.noResult}
              noResultMessage={commonLabels.searchCriteria}
              priorityResults={[]}
              isLastColumnSticky
            />
          </MuiGrid>
        </MuiGrid>
      </MuiGrid>
    </GenericAccountsSearchResultWrapper>
  );
};
